/**
 * Phase 1 state machine for Alma's two-phase /try chat. Pure: no React, no
 * network, no clock reads (today and now come in on actions), so it can be
 * unit-tested and replayed from a saved draft.
 *
 * The chat side effects are an outbox: each transition queues the traveler's
 * recap ("you" bubble) and Alma's next scripted line. TryChat shows them in
 * order — Alma's after a typing pause — and acks each one. The sheet stays
 * hidden while anything is queued, so the question never jumps ahead of the
 * line that introduces it.
 */
import { OTHER_CHOICE_ID } from '../../components/design-system/flow/ChoiceChips';
import { earliestStart } from './calendar';
import { checkRanges, daysLine, isIsoDate, legsFromRanges, rangeRows, rebuildDates, type CityDates } from './dates';
import {
  ALMA_LINES,
  BRANCH_PRIORITY_LIMIT,
  COMING_SOON,
  FLOW_VERSION,
  GROUP_OPTIONS,
  HEADCOUNT,
  HEADCOUNT_LABEL,
  NAME_MAX_LENGTH,
  OTHER_LABEL,
  STEP_COPY,
  WALKING_HELPER,
  fill,
} from './flowConfig';
import { picksRecap, pickLabel, walkingLabel, whereWhenRecap, whoRecap } from './recap';
import type { AlmaFlowAnswers, FlowBranch, FlowGroup, FlowLists, FlowMessage, StepId } from './types';

export { OTHER_CHOICE_ID };

/** Counted steps in order (`name` is asked first when needed but never counted). */
const STEP_ORDER: StepId[] = ['where_when', 'who', 'priorities', 'branch', 'walking'];

export type FlowStep = StepId | 'handoff';

export interface FlowState {
  version: string;
  persona: string;
  step: FlowStep;
  /** Which follow-up question `step: 'branch'` is on (its question_key). */
  branchKey: string | null;
  /** 2 once TryChat has handed over to Alma's conversation. */
  phase: 1 | 2;
  /** Anonymous traveler's typed name, or a signed-in traveler's first name. */
  name: string;
  // Working inputs for the current and earlier steps.
  cityIds: string[];
  /** First and last day per city id, so dates follow their city when the order changes. */
  dates: Record<string, Partial<CityDates>>;
  group: FlowGroup | null;
  groupSize: number;
  priorityIds: string[];
  priorityOther: string;
  /** Working picks per follow-up question, by question_key. */
  branchWork: Record<string, { ids: string[]; other: string }>;
  /** Id of the traveler's recap bubble for each finished step (follow-ups as `branch:<key>`), so going back can drop it and everything after it from the chat. */
  recapIds: Record<string, number>;
  /** Snapshot of each completed step — labels included, so a later rename or deactivation can't change them. */
  answers: Partial<AlmaFlowAnswers>;
  /** Progress denominator. Only ever shrinks. */
  total: number;
  outbox: FlowMessage[];
  nextMessageId: number;
  startedAt: number;
  /** Index in the chat transcript where Phase 2 starts (the model only sees from here on). */
  phase2From: number | null;
  /** When Phase 2 started. */
  phase2StartedAt: number | null;
  /** Alma's main questions asked so far in Phase 2 (counted by the question marker). */
  p2Questions: number;
  /** When Alma closed Phase 2 (the completion marker arrived). */
  completedAt: number | null;
  /** Not saved in the draft; reloaded on resume. */
  lists: FlowLists;
}

export type FlowAction =
  | { type: 'lists'; lists: FlowLists }
  | { type: 'submitName'; name: string }
  | { type: 'setCities'; ids: string[] }
  | { type: 'setDate'; index: number; value: string }
  /** A city's first and last day, from the calendar. `to` is '' while only the first day is picked. */
  | { type: 'setRange'; city: number; from: string; to: string }
  | { type: 'completeWhereWhen'; today: string }
  | { type: 'pickGroup'; group: FlowGroup }
  | { type: 'setGroupSize'; size: number }
  | { type: 'completeWho' }
  | { type: 'setPriorities'; ids: string[] }
  | { type: 'setPriorityOther'; text: string }
  | { type: 'completePriorities' }
  | { type: 'setBranchPicks'; ids: string[] }
  | { type: 'setBranchOther'; text: string }
  | { type: 'completeBranch' }
  | { type: 'goBack' }
  | { type: 'pickWalking'; id: string }
  | { type: 'ack'; id: number }
  | { type: 'startPhase2'; messageIndex: number; now: number }
  | { type: 'questionAsked' }
  | { type: 'complete'; now: number };

// ── List availability ────────────────────────────────────────────────────────

/** Loaded and empty — the step is skipped. A failed list is never "empty". */
function emptyList(lists: FlowLists, key: 'cities' | 'specialties' | 'walking'): boolean {
  return lists[key].length === 0 && !lists.failed.includes(key);
}

/** A branch can run only if its specialty is offered and it has options. */
function viableBranches(lists: FlowLists): FlowBranch[] {
  const slugs = new Set(lists.specialties.map((s) => s.slug));
  return lists.branches.filter((b) => slugs.has(b.trigger_specialty_slug) && b.options.length > 0);
}

/** No cities to offer: the flow can't book anything, so TryChat falls back to the classic chat. */
export function flowUnavailable(lists: FlowLists): boolean {
  return emptyList(lists, 'cities');
}

/** A step as the header counts it. Follow-up questions are one step each, told apart by `branchKey`. */
export interface StepRef {
  step: StepId;
  branchKey?: string;
}

const at = (step: StepId, branchKey?: string | null): StepRef => ({ step, ...(branchKey ? { branchKey } : {}) });
const sameStep = (a: StepRef, b: StepRef) => a.step === b.step && a.branchKey === b.branchKey;
const recapKey = (ref: StepRef) => (ref.branchKey ? `branch:${ref.branchKey}` : ref.step);
/** Stands in for a follow-up whose questions failed to load (it shows a retry). */
const RETRY_KEY = 'retry';

function currentRef(state: Pick<FlowState, 'step' | 'branchKey'>): StepRef | null {
  return state.step === 'name' || state.step === 'handoff' ? null : at(state.step, state.branchKey);
}

function moveTo(state: FlowState, to: StepRef | null): FlowState {
  return { ...state, step: to?.step ?? 'handoff', branchKey: to?.branchKey ?? null };
}

/** The follow-up questions the traveler's top priorities open, in priority order (each question once). */
export function chosenBranches(state: Pick<FlowState, 'answers' | 'lists'>): FlowBranch[] {
  const viable = viableBranches(state.lists);
  const out: FlowBranch[] = [];
  for (const p of (state.answers.priorities ?? []).slice(0, BRANCH_PRIORITY_LIMIT)) {
    const branch = p.slug ? viable.find((b) => b.trigger_specialty_slug === p.slug) : undefined;
    if (branch && !out.includes(branch)) out.push(branch);
  }
  return out;
}

/** Steps that will show, given what's loaded and answered so far. */
export function countedSteps(state: Pick<FlowState, 'answers' | 'lists'>): StepRef[] {
  const { lists } = state;
  const steps: StepRef[] = [at('where_when'), at('who')];
  if (!emptyList(lists, 'specialties')) {
    steps.push(at('priorities'));
    const priorities = state.answers.priorities;
    if (priorities) {
      const branches = chosenBranches(state);
      for (const b of branches) steps.push(at('branch', b.question_key));
      // Branches failed to load: keep a step (it shows a retry) rather than silently skipping them.
      if (branches.length === 0 && lists.failed.includes('branches') && priorities.slice(0, BRANCH_PRIORITY_LIMIT).some((p) => p.slug)) {
        steps.push(at('branch', RETRY_KEY));
      }
    } else {
      // Unknown until priorities are answered: count the most it could be, so the total only ever shrinks.
      const most = Math.min(BRANCH_PRIORITY_LIMIT, viableBranches(lists).length) || (lists.failed.includes('branches') ? 1 : 0);
      for (let i = 0; i < most; i++) steps.push(at('branch', `?${i}`));
    }
  }
  if (!emptyList(lists, 'walking')) steps.push(at('walking'));
  return steps;
}

/** Header fraction, or null while asking the name and after the hand-off. */
export function flowProgress(state: FlowState): { current: number; total: number } | null {
  const ref = currentRef(state);
  if (!ref) return null;
  const current = countedSteps(state).findIndex((s) => sameStep(s, ref)) + 1;
  return current > 0 ? { current, total: state.total } : null;
}

// ── Transitions ──────────────────────────────────────────────────────────────

function queue(state: FlowState, role: FlowMessage['role'], text: string): FlowState {
  return {
    ...state,
    outbox: [...state.outbox, { id: state.nextMessageId, role, text }],
    nextMessageId: state.nextMessageId + 1,
  };
}

/** The step after `from`, or null at the hand-off. `from` may no longer be counted (its list vanished). */
function nextStepAfter(state: FlowState, from: StepRef): StepRef | null {
  const steps = countedSteps(state);
  const index = steps.findIndex((s) => sameStep(s, from));
  if (index !== -1) return steps[index + 1] ?? null;
  const order = STEP_ORDER.indexOf(from.step);
  return steps.find((s) => STEP_ORDER.indexOf(s.step) > order) ?? null;
}

/** Alma's scripted line introducing `to` (the welcome line introduces where_when). */
function lineFor(state: FlowState, to: StepRef | null): string | null {
  switch (to?.step) {
    case 'who': return ALMA_LINES.who;
    case 'priorities': return ALMA_LINES.priorities;
    case 'branch': return chosenBranches(state).find((b) => b.question_key === to.branchKey)?.alma_line ?? null;
    case 'walking': return state.answers.branches?.length ? ALMA_LINES.walkingAfterBranch : ALMA_LINES.walking;
    default: return null;
  }
}

function withTotal(state: FlowState): FlowState {
  return { ...state, total: Math.min(state.total, countedSteps(state).length) };
}

/** Records a finished step: the recap bubble, then the next step and its line. */
function advance(state: FlowState, from: StepRef, recap: string): FlowState {
  const recapIds = { ...state.recapIds, [recapKey(from)]: state.nextMessageId };
  let next = withTotal(queue({ ...state, recapIds }, 'user', recap));
  const to = nextStepAfter(next, from);
  next = moveTo(next, to);
  const line = lineFor(next, to);
  return line ? queue(next, 'assistant', line) : next;
}

/** Skips the current step if its list turned out empty (e.g. after a retry), without a recap. */
function skipIfEmpty(state: FlowState): FlowState {
  const current = currentRef(state);
  if (!current || countedSteps(state).some((s) => sameStep(s, current))) return withTotal(state);
  const to = nextStepAfter(state, current);
  const next = withTotal(moveTo(state, to));
  const line = lineFor(next, to);
  return line ? queue(next, 'assistant', line) : next;
}

/** Valid picks in order; an empty "Other" doesn't count. */
function validPicks(ids: string[], otherText: string): string[] {
  return ids.filter((id) => id !== OTHER_CHOICE_ID || otherText.trim() !== '');
}

/**
 * Where "back" goes from here: the previous counted step, and the id of the
 * first chat line to drop (that step's recap bubble — everything after it
 * belongs to the steps being undone). Null on the first step, once Phase 2
 * has started, or while lines are still queued.
 */
export function rewindTarget(state: FlowState): { step: StepId; branchKey?: string; fromMessageId: number } | null {
  const ref = currentRef(state);
  if (!ref || state.phase !== 1 || state.outbox.length > 0) return null;
  const steps = countedSteps(state);
  const target = steps[steps.findIndex((s) => sameStep(s, ref)) - 1];
  const fromMessageId = target ? state.recapIds[recapKey(target)] : undefined;
  return target && fromMessageId !== undefined ? { ...target, fromMessageId } : null;
}

export interface FlowTraveler {
  signedIn: boolean;
  /** First name from the profile, when signed in. */
  name: string | null;
}

/** A fresh flow: anonymous travelers get the name question first. */
export function startFlow(lists: FlowLists, persona: string, traveler: FlowTraveler, now: number): FlowState {
  const base: FlowState = {
    version: FLOW_VERSION,
    persona,
    step: traveler.signedIn ? 'where_when' : 'name',
    phase: 1,
    name: traveler.signedIn ? (traveler.name ?? '') : '',
    cityIds: [],
    dates: {},
    group: null,
    groupSize: HEADCOUNT.defaultValue,
    priorityIds: [],
    priorityOther: '',
    branchKey: null,
    branchWork: {},
    recapIds: {},
    answers: {},
    total: 0,
    outbox: [],
    nextMessageId: 1,
    startedAt: now,
    phase2From: null,
    phase2StartedAt: null,
    p2Questions: 0,
    completedAt: null,
    lists,
  };
  const state = { ...base, total: countedSteps(base).length };
  if (!traveler.signedIn) return queue(state, 'assistant', fill(ALMA_LINES.askName, { persona }));
  const welcome = traveler.name ? ALMA_LINES.welcomeKnown : ALMA_LINES.welcomeUnknown;
  return queue(state, 'assistant', fill(welcome, { persona, name: traveler.name ?? '' }));
}

/** Drops working picks that no longer exist after a list reload. Completed answers keep their saved labels. */
function sanitize(state: FlowState, lists: FlowLists): FlowState {
  const cityIds = lists.failed.includes('cities') ? state.cityIds : state.cityIds.filter((id) => lists.cities.some((c) => c.id === id && c.bookable !== false));
  const priorityIds = lists.failed.includes('specialties')
    ? state.priorityIds
    : state.priorityIds.filter((id) => id === OTHER_CHOICE_ID || lists.specialties.some((s) => s.id === id));
  return { ...state, lists, cityIds, priorityIds, dates: rebuildDates(cityIds, state.dates) };
}

export function flowReducer(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case 'lists':
      return skipIfEmpty(sanitize(state, action.lists));

    case 'goBack': {
      const target = rewindTarget(state);
      // Answers stay as the working inputs, so the earlier question reopens prefilled.
      return target ? { ...state, step: target.step, branchKey: target.branchKey ?? null } : state;
    }

    case 'ack':
      return { ...state, outbox: state.outbox.filter((m) => m.id !== action.id) };

    case 'startPhase2':
      return state.step === 'handoff' && state.phase === 1 ? { ...state, phase: 2, phase2From: action.messageIndex, phase2StartedAt: action.now } : state;

    case 'questionAsked':
      return state.phase === 2 && !state.completedAt ? { ...state, p2Questions: state.p2Questions + 1 } : state;

    case 'complete':
      return state.phase === 2 && !state.completedAt ? { ...state, completedAt: action.now } : state;

    case 'submitName': {
      const name = action.name.trim().slice(0, NAME_MAX_LENGTH);
      if (state.step !== 'name' || !name) return state;
      // The name was typed in the composer, so it's already the traveler's own message — no recap.
      const next = { ...state, name, answers: { ...state.answers, traveler_name: name }, step: 'where_when' as const };
      return queue(next, 'assistant', fill(ALMA_LINES.welcomeNamed, { name, persona: state.persona }));
    }

    case 'setCities': {
      if (state.step !== 'where_when') return state;
      // A city that isn't open yet can't be picked, whatever the screen sent.
      const ids = action.ids.filter((id) => !state.lists.cities.some((c) => c.id === id && c.bookable === false));
      return { ...state, cityIds: ids, dates: rebuildDates(ids, state.dates) };
    }

    case 'setDate': {
      if (state.step !== 'where_when') return state;
      // Rows come two per city: its first day, then its last day.
      const id = state.cityIds[Math.floor(action.index / 2)];
      if (!id) return state;
      const field = action.index % 2 === 0 ? 'from' : 'to';
      return { ...state, dates: { ...state.dates, [id]: { ...state.dates[id], [field]: action.value } } };
    }

    case 'setRange': {
      if (state.step !== 'where_when') return state;
      const id = state.cityIds[action.city];
      if (!id) return state;
      // A later city whose dates no longer follow this one is cleared, so the stays never overlap.
      const rebuilt = rebuildDates(state.cityIds, { ...state.dates, [id]: { from: action.from, to: action.to } });
      // A stay is picked as a whole: a city left with a last day but no first day loses both.
      const dates = Object.fromEntries(Object.entries(rebuilt).filter(([, d]) => d.from));
      return { ...state, dates };
    }

    case 'completeWhereWhen': {
      if (state.step !== 'where_when' || state.cityIds.length === 0) return state;
      const rows = rangeRows(state.cityIds, state.dates);
      if (!checkRanges(rows, action.today).complete) return state;
      const cities = state.cityIds.map((id) => state.lists.cities.find((c) => c.id === id)).filter((c) => !!c);
      if (cities.length !== state.cityIds.length) return state;
      const trip = legsFromRanges(cities.map((c) => c.name), rows);
      const answers = {
        ...state.answers,
        destinations: cities.map((c) => ({ id: c.id, name: c.name })),
        destination_days: trip.legs,
        start_date: trip.start_date,
        end_date: trip.end_date,
        trip_days: trip.trip_days,
      };
      return advance({ ...state, answers }, at('where_when'), whereWhenRecap(trip.legs));
    }

    case 'pickGroup': {
      if (state.step !== 'who') return state;
      if (action.group === 'solo' || action.group === 'partner') {
        const size = action.group === 'solo' ? 1 : 2;
        const answers = { ...state.answers, group: action.group, group_size: size };
        return advance({ ...state, group: action.group, groupSize: size, answers }, at('who'), whoRecap(action.group, size));
      }
      const groupSize = Math.min(HEADCOUNT.max, Math.max(HEADCOUNT.min, state.groupSize));
      return { ...state, group: action.group, groupSize };
    }

    case 'setGroupSize':
      if (state.step !== 'who') return state;
      return { ...state, groupSize: Math.min(HEADCOUNT.max, Math.max(HEADCOUNT.min, action.size)) };

    case 'completeWho': {
      if (state.step !== 'who' || (state.group !== 'friends' && state.group !== 'family')) return state;
      const answers = { ...state.answers, group: state.group, group_size: state.groupSize };
      return advance({ ...state, answers }, at('who'), whoRecap(state.group, state.groupSize));
    }

    case 'setPriorities':
      return state.step === 'priorities' ? { ...state, priorityIds: action.ids } : state;

    case 'setPriorityOther':
      return state.step === 'priorities' ? { ...state, priorityOther: action.text } : state;

    case 'completePriorities': {
      if (state.step !== 'priorities') return state;
      const picks = validPicks(state.priorityIds, state.priorityOther);
      if (picks.length === 0) return state;
      const priorities = picks.map((id, i) => {
        if (id === OTHER_CHOICE_ID) return { rank: i + 1, label: OTHER_LABEL, custom: state.priorityOther.trim() };
        const s = state.lists.specialties.find((x) => x.id === id);
        return { rank: i + 1, specialty_id: id, slug: s?.slug, label: s?.label ?? id };
      });
      const answers = { ...state.answers, priorities };
      // A follow-up answer only applies while its priority is still among the top few.
      const keep = new Set(chosenBranches({ answers, lists: state.lists }).map((b) => b.question_key));
      if (answers.branches) answers.branches = answers.branches.filter((b) => keep.has(b.question_key));
      if (!answers.branches?.length) delete answers.branches;
      const branchWork = Object.fromEntries(Object.entries(state.branchWork).filter(([key]) => keep.has(key)));
      return advance({ ...state, answers, branchWork }, at('priorities'), picksRecap(priorities.map(pickLabel)));
    }

    case 'setBranchPicks': {
      const key = state.branchKey;
      if (state.step !== 'branch' || !key) return state;
      return { ...state, branchWork: { ...state.branchWork, [key]: { ids: action.ids, other: state.branchWork[key]?.other ?? '' } } };
    }

    case 'setBranchOther': {
      const key = state.branchKey;
      if (state.step !== 'branch' || !key) return state;
      return { ...state, branchWork: { ...state.branchWork, [key]: { ids: state.branchWork[key]?.ids ?? [], other: action.text } } };
    }

    case 'completeBranch': {
      const key = state.branchKey;
      const all = chosenBranches(state);
      const branch = all.find((b) => b.question_key === key);
      if (state.step !== 'branch' || !key || !branch) return state;
      const work = state.branchWork[key] ?? { ids: [], other: '' };
      const ids = validPicks(work.ids, work.other);
      if (ids.length === 0) return state;
      const picks = ids.map((id) => {
        if (id === OTHER_CHOICE_ID) return { label: OTHER_LABEL, custom: work.other.trim() };
        const o = branch.options.find((x) => x.id === id);
        return { option_id: id, slug: o?.slug, label: o?.label ?? id };
      });
      const entry = { question_key: branch.question_key, trigger_specialty_slug: branch.trigger_specialty_slug, title: branch.title, picks };
      const order = all.map((b) => b.question_key);
      const branches = [...(state.answers.branches ?? []).filter((b) => b.question_key !== key), entry]
        .sort((x, y) => order.indexOf(x.question_key) - order.indexOf(y.question_key));
      return advance({ ...state, answers: { ...state.answers, branches } }, at('branch', key), picksRecap(picks.map(pickLabel)));
    }

    case 'pickWalking': {
      if (state.step !== 'walking') return state;
      const option = state.lists.walking.find((o) => o.id === action.id);
      if (!option) return state;
      const label = walkingLabel(option, state.group);
      const answers = { ...state.answers, walking_comfort: { option_id: option.id, slug: option.slug, label } };
      return advance({ ...state, answers }, at('walking'), label);
    }
  }
}

/** The finished answers (Phase 1 done), or null while still asking. */
export function flowAnswers(state: FlowState): AlmaFlowAnswers | null {
  const a = state.answers;
  if (state.step !== 'handoff' || !a.destinations || !a.destination_days || !a.start_date || !a.end_date || !a.trip_days || !a.group || !a.group_size) {
    return null;
  }
  return {
    ...a,
    destinations: a.destinations,
    destination_days: a.destination_days,
    start_date: a.start_date,
    end_date: a.end_date,
    trip_days: a.trip_days,
    group: a.group,
    group_size: a.group_size,
    priorities: a.priorities ?? [],
    phase: state.phase,
    flow_version: state.version,
  };
}

// ── What the sheet shows ─────────────────────────────────────────────────────

/** One city's stay on the "Where and when" step: its calendar row. */
export interface RangeView {
  /** The city. */
  label: string;
  from: string;
  to: string;
  /** Earliest day its calendar lets the traveler pick. */
  min: string;
  /** The city before it has no dates yet, so this one can't be opened. */
  locked: boolean;
  error?: string;
}

export type SheetView =
  | { step: 'name' }
  | {
      step: 'where_when';
      title: string;
      helper: string;
      options: { id: string; label: string; disabled?: boolean }[];
      value: string[];
      ranges: RangeView[];
      daysNote: string;
      canContinue: boolean;
      error: boolean;
    }
  | { step: 'who'; title: string; options: { id: FlowGroup; label: string }[]; value: FlowGroup | null; showCount: boolean; countLabel: string; count: number }
  | {
      step: 'priorities' | 'branch';
      title: string;
      helper?: string;
      options: { id: string; label: string }[];
      value: string[];
      otherText: string;
      canContinue: boolean;
      error: boolean;
      /** Which follow-up question this is (branch step only). */
      questionKey?: string;
    }
  | { step: 'walking'; title: string; helper: string; options: { id: string; label: string }[]; error: boolean }
  | { step: 'handoff' };

/** Everything the current sheet needs to render. `today` drives date validation. */
export function sheetView(state: FlowState, today: string): SheetView {
  const { lists } = state;
  switch (state.step) {
    case 'name':
    case 'handoff':
      return { step: state.step };

    case 'where_when': {
      const names = state.cityIds.map((id) => lists.cities.find((c) => c.id === id)?.name ?? '');
      const rows = rangeRows(state.cityIds, state.dates);
      const check = checkRanges(rows, today);
      return {
        step: 'where_when',
        ...STEP_COPY.where_when,
        options: lists.cities.map((c) => (c.bookable === false ? { id: c.id, label: `${c.name}${COMING_SOON}`, disabled: true } : { id: c.id, label: c.name })),
        value: state.cityIds,
        ranges: names.map((label, i) => {
          const previous = state.dates[state.cityIds[i - 1]];
          return {
            label,
            from: rows[i * 2],
            to: rows[i * 2 + 1],
            min: earliestStart(today, previous?.to),
            locked: i > 0 && !(isIsoDate(previous?.from) && isIsoDate(previous?.to)),
            error: check.errors[i * 2] ?? check.errors[i * 2 + 1],
          };
        }),
        daysNote: check.complete ? daysLine(legsFromRanges(names, rows).legs) : '',
        canContinue: names.length > 0 && check.complete,
        error: lists.failed.includes('cities'),
      };
    }

    case 'who': {
      const showCount = state.group === 'friends' || state.group === 'family';
      return {
        step: 'who',
        title: STEP_COPY.who.title,
        options: GROUP_OPTIONS,
        value: state.group,
        showCount,
        countLabel: HEADCOUNT_LABEL,
        count: state.groupSize,
      };
    }

    case 'priorities':
      return {
        step: 'priorities',
        ...STEP_COPY.priorities,
        options: lists.specialties.map((s) => ({ id: s.id, label: s.label })),
        value: state.priorityIds,
        otherText: state.priorityOther,
        canContinue: validPicks(state.priorityIds, state.priorityOther).length > 0,
        error: lists.failed.includes('specialties'),
      };

    case 'branch': {
      const branch = chosenBranches(state).find((b) => b.question_key === state.branchKey);
      const work = (state.branchKey && state.branchWork[state.branchKey]) || { ids: [], other: '' };
      return {
        step: 'branch',
        title: branch?.title ?? '',
        helper: branch?.helper ?? undefined,
        options: branch?.options.map((o) => ({ id: o.id, label: o.label })) ?? [],
        value: work.ids,
        otherText: work.other,
        canContinue: !!branch && validPicks(work.ids, work.other).length > 0,
        error: lists.failed.includes('branches'),
        // Lets the sheet start fresh for each follow-up question.
        ...(state.branchKey ? { questionKey: state.branchKey } : {}),
      };
    }

    case 'walking':
      return {
        step: 'walking',
        title: STEP_COPY.walking.title,
        helper: WALKING_HELPER[state.group ?? 'solo'],
        options: lists.walking.map((o) => ({ id: o.id, label: walkingLabel(o, state.group) })),
        error: lists.failed.includes('walking'),
      };
  }
}
